import { getColourForRainfall } from 'common/graph-utils/get-rainfall';
import { IconNameV2 } from 'common/icon/iconV2';
import variables from 'styles/base/variables.module.scss';
import { StatusRating } from 'utilities/status/status-rating.js';
import { NO_DATA_BACKGROUND_COLOR } from 'utilities/weather-station/location';
import { getBackgroundColorSoilMoisture } from '../marker-view/main/weatherStation/station-utils-soil-moisture';
import { getBackgroundColorSoilTemperature } from '../marker-view/main/weatherStation/station-utils-soil-temp';
import { getBackgroundForTemp } from '../marker-view/main/weatherStation/utils.temperature';

export type MarkerOptions = {
  border?: string;
  background?: string;
  text?: string;
  /** override dot background */
  dot?: string;
  icon?: IconNameV2;
};

export type MarkerOptionsMap = Record<string, MarkerOptions>;

export const weatherStationTemp = 'WS-TEMP';
export const weatherStationRainfall = 'WS-RAINFALL';
export const weatherStationSoilMoisture = 'WS-SOIL-MOISTURE';
export const weatherStationSoilTemp = 'WS-SOIL-TEMP';

const getMarkerThemes = ({
  tags,
  markerThemes,
  defaultTheme,
}: {
  tags: string[];
  markerThemes?: MarkerOptionsMap;
  defaultTheme?: Partial<MarkerOptions>;
}) => {
  /** check if theme is in additional themes */
  if (markerThemes && tags.length) {
    const markerThemesKeys = Object.keys(markerThemes);
    const [intersection] = tags.filter((tag) => markerThemesKeys.indexOf(tag) !== -1);
    if (intersection) {
      return { ...defaultTheme, ...markerThemes[intersection] };
    }
  }
  return defaultTheme;
};

export const getMarkerV2ThemedPill = ({
  tags,
  meta,
  markerThemes,
  defaultTheme,
}: {
  tags?: string[];
  meta?: StatusRating | string;
  icon: IconNameV2 | undefined;
  markerThemes?: MarkerOptionsMap;
  defaultTheme?: Partial<MarkerOptions>;
}): MarkerOptions | undefined => {
  if (tags) {
    const markerUpdatedThemes = getMarkerThemes({ tags, markerThemes, defaultTheme });

    if (tags.includes(weatherStationSoilTemp)) {
      const val = parseFloat(meta ?? '');
      const colour = isNaN(val) ? NO_DATA_BACKGROUND_COLOR : getBackgroundColorSoilTemperature(val);
      const icon: IconNameV2 = 'soil-temperature';
      return { ...markerUpdatedThemes, border: colour, dot: colour, icon };
    }
    if (tags.includes(weatherStationSoilMoisture)) {
      const val = parseInt(meta ?? '');
      const colour = isNaN(val) ? NO_DATA_BACKGROUND_COLOR : getBackgroundColorSoilMoisture(val);
      const icon: IconNameV2 = 'soil-moisture';
      return { ...markerUpdatedThemes, border: colour, dot: colour, icon };
    }
    if (tags.includes(weatherStationTemp)) {
      const val = Math.round(parseFloat(meta ?? '') * 10) / 10;
      const colour = isNaN(val) ? NO_DATA_BACKGROUND_COLOR : getBackgroundForTemp(val);
      const icon: IconNameV2 = 'temperature';
      return { ...markerUpdatedThemes, border: colour, dot: colour, icon };
    }
    if (tags.includes(weatherStationRainfall)) {
      const val = Math.round(parseFloat(meta ?? '') * 10) / 10;
      const colour = isNaN(val) ? NO_DATA_BACKGROUND_COLOR : getColourForRainfall(val);
      const icon: IconNameV2 = 'rain';
      return { ...markerUpdatedThemes, border: colour, dot: colour, icon };
    }

    /** fallover to any hardcoded themes */
    if (tags.includes('status-background')) {
      if (meta === StatusRating.statusNone) {
        return {
          border: '#FFFFFF',
          background: '#FFFFFF',
          text: '#A6ABB7',
        };
      }
      if (meta === StatusRating.statusBad) {
        return {
          border: variables['status-bad-text'],
          background: variables['status-bad-icon'],
          text: '#ffffff',
        };
      }
      if (meta === StatusRating.statusMedium) {
        return {
          border: variables['status-medium-text'],
          background: variables['status-medium-icon'],
          text: '#ffffff',
        };
      }
      if (meta === StatusRating.statusMediumGood) {
        return {
          border: variables['status-medium-good-text'],
          background: variables['status-medium-good-icon'],
          text: '#ffffff',
        };
      }
      if (meta === StatusRating.statusGood) {
        return {
          border: variables['status-good-text'],
          background: variables['status-good-icon'],
          text: '#ffffff',
        };
      }
    }
    if (tags.includes('white-slate')) {
      return {
        border: '#FFFFFF',
        background: '#FFFFFF',
        text: '#A6ABB7',
      };
    }
    if (tags.includes('coastal-blue')) {
      return {
        border: variables['coastal'],
        background: variables['coastal'],
        text: '#ffffff',
      };
    }
    if (tags.includes('tides')) {
      return {
        border: variables['tides'],
        background: variables['tides'],
        text: '#ffffff',
      };
    }
    return markerUpdatedThemes;
  }
  return defaultTheme;
};
